'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { putAffiliateProgramSignUpApi } from '@flipster/apis/v1/affiliate-program';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { NoSSR } from '@flipster/react/misc/lifecycle/NoSSR';
import { css } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import { FormItem, Input, Link } from '@flipster/ui';
import { alert } from '@flipster/ui/Alert';

import { CheckBoxGroup } from './CheckBoxGroup';
import { AffiliateTypeItems, FollowersItems, HearAboutItems, PromotedChannelItems } from './constants';
import { AffiliateSchema, affiliateValidator } from './validator';

const sectionFormItem = css({
  '& + &': {
    mt: 24,
  },
});

const sectionFormLabel = css({
  _after: {
    content: '"*"',
    ml: 4,
    color: 'content.state.negative',
  },
});

const sectionLink = css({
  px: '6px',
  color: 'content.accent.info',
});

const Form = () => {
  const t = useTranslations();
  const [sizeOfSocialFollowing, setSizeOfSocialFollowing] = useState<string>();

  const methods = useForm<AffiliateSchema>({
    mode: 'onTouched',
    resolver: yupResolver(affiliateValidator),
    defaultValues: {
      typesOfAffiliate: [],
      channelsToPromotePrex: [],
      howFoundPrex: [],
    },
  });

  const {
    handleSubmit,
    watch,
    register,
    formState: { isValid },
  } = methods;

  const submit = async (formValues: AffiliateSchema) => {
    try {
      await putAffiliateProgramSignUpApi({
        ...formValues,
        typesOfAffiliate: formValues.typesOfAffiliate.join('|'),
        channelsToPromotePrex: formValues.channelsToPromotePrex.join('|'),
        howFoundPrex: formValues.howFoundPrex.join('|'),
      });
      snackbar.success({ message: 'Flipster Affiliate Program application submitted' });
    } catch (err) {
      alert.error(err);
    }
  };

  const isActiveSubmit = isValid && !!watch('agreeToAffiliateProgramTermsAndPolicy');

  return (
    <section
      className={css({
        maxW: 600,
        marginX: 'auto',
        mt: 24,
        pb: 138,
        px: 16,
        tablet: {
          mt: 80,
          pb: 150,
          px: 0,
        },
      })}
    >
      <h1 className={css({ textStyle: ['Title/M', 'Display/L'], mb: 24 })}>
        <FormattedMessage id='Autogen_common.r251126.s_Apply_for_Flipster_Affiliate_Program' />
      </h1>
      <p className={css({ textStyle: 'Text/L', color: 'content.default.level-3', mb: [24, 40] })}>
        <FormattedMessage id='Autogen_common.r251126.s_Thank_you_for_your_interest_in_joining_the_Flipster_Affiliate_Program_Please_fill_up_the_form_below_to_submit_your_application_for_consideration_Our_team_will_review_your_submission_and_notify_you_o' />
      </p>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <FormItem name='uid' className={sectionFormItem} label={<p className={sectionFormLabel}>UID</p>}>
            <Input placeholder={t('Autogen_common.r251126.s_Your_answer')} />
          </FormItem>
          <FormItem
            name='telegramHandle'
            className={sectionFormItem}
            label={t('Autogen_common.r251126.s_Telegram_Handle')}
          >
            <Input placeholder={t('Autogen_common.r251126.s_Your_answer')} />
          </FormItem>
          <FormItem
            className={sectionFormItem}
            label={
              <p className={sectionFormLabel}>
                <FormattedMessage id='Autogen_common.r251126.s_Type_of_Affiliate' />
              </p>
            }
          >
            <CheckBoxGroup name='typesOfAffiliate' items={AffiliateTypeItems} />
          </FormItem>
          <FormItem
            name='otherExchangesPromoted'
            className={sectionFormItem}
            label={
              <p className={sectionFormLabel}>
                <FormattedMessage id='Autogen_common.r251126.s_Exchange_s_you_ve_promoted_If_none_put_N_A' />
              </p>
            }
          >
            <Input placeholder={t('Autogen_common.r251126.s_Your_answer')} />
          </FormItem>
          <FormItem
            className={sectionFormItem}
            label={
              <p className={sectionFormLabel}>
                <FormattedMessage id='Autogen_common.r251126.s_Your_Promotion_Channels_for_Flipster' />
              </p>
            }
          >
            <CheckBoxGroup name='channelsToPromotePrex' items={PromotedChannelItems} />
          </FormItem>
          <FormItem
            name='socialLinks'
            className={sectionFormItem}
            label={t('Autogen_common.r251126.s_Your_Social_Media_Profile_Links')}
          >
            <Input placeholder={t('Autogen_common.r251126.s_Your_answer')} />
          </FormItem>
          <FormItem
            className={sectionFormItem}
            label={
              <p className={sectionFormLabel}>
                <FormattedMessage id='Autogen_common.r251126.s_Size_of_your_social_media_following' />
              </p>
            }
          >
            <div className={flex({ flexDirection: 'column', align: 'flex-start', gap: 8 })}>
              {FollowersItems.map((item) => (
                <RadioButton
                  key={item.value}
                  {...register('sizeOfSocialFollowing')}
                  name='sizeOfSocialFollowing'
                  value={item.value}
                  onChange={(e) => setSizeOfSocialFollowing(e.currentTarget.value)}
                  checked={sizeOfSocialFollowing === item.value}
                >
                  {item.label}
                </RadioButton>
              ))}
            </div>
          </FormItem>
          <FormItem
            className={sectionFormItem}
            label={
              <p className={sectionFormLabel}>
                <FormattedMessage id='Autogen_common.r251126.s_How_did_you_learn_about_Flipster' />
              </p>
            }
          >
            <CheckBoxGroup name='howFoundPrex' items={HearAboutItems} />
          </FormItem>
          <FormItem
            name='prexBizdevContact'
            className={sectionFormItem}
            label={
              <p className={sectionFormLabel}>
                <FormattedMessage id='Autogen_common.r251126.s_Your_Flipster_BD_contact_Enter_N_A_if_none' />
              </p>
            }
          >
            <Input placeholder={t('Autogen_common.r251126.s_Your_answer')} />
          </FormItem>
          <p
            className={css({
              p: 12,
              bgColor: 'background.default.level-3',
              rounded: 8,
              textStyle: 'Text/M',
              color: 'content.default.level-3',
              my: 24,
            })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_By_ticking_the_checkbox_below_and_submitting_this_form_you_are_confirming_that_you_have_read_and_agree_to_our_Affiliate_Program_Terms_and_Affiliate_Program_Policy' />
          </p>
          <FormItem name='agreeToAffiliateProgramTermsAndPolicy' className={sectionFormItem}>
            <Checkbox>
              I have read and agree to the
              <NoSSR>
                <Link
                  href={zendesk.getUrl('AffiliateProgramTerms')}
                  shape={isActiveSubmit ? 'link1' : 'trans'}
                  underline={isActiveSubmit}
                  className={sectionLink}
                  opener
                >
                  <FormattedMessage id='Autogen_common.r251126.s_Affiliate_Program_Terms' />
                </Link>
              </NoSSR>
              and
              <NoSSR>
                <Link
                  href={zendesk.getUrl('AffiliateProgramPrivacyPolicy')}
                  shape={isActiveSubmit ? 'link1' : 'trans'}
                  underline={isActiveSubmit}
                  className={sectionLink}
                  opener
                >
                  <FormattedMessage id='Autogen_common.r251126.s_Affiliate_Program_Policy' />
                </Link>
              </NoSSR>
            </Checkbox>
          </FormItem>
          <div
            className={center({
              position: 'fixed',
              left: 0,
              bottom: 0,
              w: '100%',
              mt: 0,
              px: 16,
              py: 20,
              bgColor: 'background.default.level-0',
              textAlign: 'center',
              tablet: { position: 'static', mt: 40, px: 0, py: 0, maxW: 600 },
            })}
          >
            <Button
              css={{
                w: '100%',
                maxW: 600,
                tablet: {
                  w: 328,
                },
              }}
              disabled={!isActiveSubmit}
              size='large'
              label={t('Autogen_common.r251126.s_Submit')}
              htmlType='submit'
            />
          </div>
        </form>
      </FormProvider>
    </section>
  );
};

export default Form;
